import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { Progress } from "../../../ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Button } from "../../../ui/button";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useNavigate } from "react-router-dom";

type Stats = {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
};

type CampaignRow = {
  id: string;
  name: string | null;
  type: string | null;
  sent_at: string | null;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
};

const pct = (num: number, denom: number) =>
  denom > 0 ? `${((num / denom) * 100).toFixed(1)}%` : "—";

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function Analytics() {
  const { merchant, credits } = useMerchant();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({ sent: 0, delivered: 0, opened: 0, clicked: 0, unsubscribed: 0 });
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!merchant) return;
    const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    const load = async () => {
      setLoading(true);

      const { data: sends } = await epicengageSupabasev1
        .from("email_sends")
        .select("id, campaign_id, status, opened_at, clicked_at, sent_at")
        .eq("merchant_id", merchant.id)
        .gte("sent_at", since);

      const sendsArr = sends ?? [];
      const sentCount = sendsArr.length;
      const deliveredCount = sendsArr.filter((s) => s.status === "delivered" || s.status === "sent").length;
      const openedCount = sendsArr.filter((s) => s.opened_at).length;
      const clickedCount = sendsArr.filter((s) => s.clicked_at).length;

      const { count: unsubCount } = await epicengageSupabasev1
        .from("customers")
        .select("id", { count: "exact", head: true })
        .eq("merchant_id", merchant.id)
        .gte("unsubscribed_at", since);

      setStats({
        sent: sentCount,
        delivered: deliveredCount,
        opened: openedCount,
        clicked: clickedCount,
        unsubscribed: unsubCount ?? 0,
      });

      const { data: camps } = await epicengageSupabasev1
        .from("campaigns")
        .select("id, name, type, sent_at, status")
        .eq("merchant_id", merchant.id)
        .not("sent_at", "is", null)
        .order("sent_at", { ascending: false });

      const rows: CampaignRow[] = (camps ?? []).map((c) => {
        const cs = sendsArr.filter((s) => s.campaign_id === c.id);
        return {
          id: c.id,
          name: c.name,
          type: c.type,
          sent_at: c.sent_at,
          sent: cs.length,
          delivered: cs.filter((s) => s.status === "delivered" || s.status === "sent").length,
          opened: cs.filter((s) => s.opened_at).length,
          clicked: cs.filter((s) => s.clicked_at).length,
        };
      });
      setCampaigns(rows);
      setLoading(false);
    };

    load();
  }, [merchant]);

  const creditsUsed = credits?.credits_used ?? 0;
  const creditsTotal = credits?.credits_total ?? 0;
  const creditsPct = creditsTotal > 0 ? (creditsUsed / creditsTotal) * 100 : 0;

  return (
      <div className="px-10 py-10">
        <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Engagement and delivery insights — last 30 days</p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Sent" value={stats.sent} />
          <StatCard label="Delivered" value={stats.delivered} sub={pct(stats.delivered, stats.sent) + " delivery rate"} />
          <StatCard label="Opened" value={stats.opened} sub={pct(stats.opened, stats.delivered) + " open rate"} />
          <StatCard label="Clicked" value={stats.clicked} sub={pct(stats.clicked, stats.opened) + " click rate"} />
          <StatCard label="Unsubscribed" value={stats.unsubscribed} />
        </div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-base">Campaign breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-sm text-muted-foreground py-6">Loading…</div>
            ) : campaigns.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-10 text-center">
                <p className="text-sm text-muted-foreground">No campaigns sent yet. Create your first campaign.</p>
                <Button className="mt-4" onClick={() => navigate("/clientpanel/enagev1/campaigns/new")}>
                  New Campaign
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Delivered</TableHead>
                    <TableHead className="text-right">Opened</TableHead>
                    <TableHead className="text-right">Clicked</TableHead>
                    <TableHead className="text-right">Open Rate</TableHead>
                    <TableHead className="text-right">Click Rate</TableHead>
                    <TableHead>Sent Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name ?? "Untitled"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">{c.type ?? "manual"}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{c.sent}</TableCell>
                      <TableCell className="text-right">{c.delivered}</TableCell>
                      <TableCell className="text-right">{c.opened}</TableCell>
                      <TableCell className="text-right">{c.clicked}</TableCell>
                      <TableCell className="text-right">{pct(c.opened, c.delivered)}</TableCell>
                      <TableCell className="text-right">{pct(c.clicked, c.opened)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(c.sent_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-base">Email credits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {creditsUsed.toLocaleString()} used of {creditsTotal.toLocaleString()}
              </span>
              <span className="font-medium">{credits?.plan_name ?? "Free"} plan</span>
            </div>
            <Progress value={creditsPct} />
            <p className="text-xs text-muted-foreground">
              Reset date: {credits?.reset_at ? formatDate(credits.reset_at) : "—"}
            </p>
          </CardContent>
        </Card>
      </div>
  );
}

const StatCard = ({ label, value, sub }: { label: string; value: number; sub?: string }) => (
  <Card>
    <CardContent className="p-5">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-3xl font-semibold mt-2">{value.toLocaleString()}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </CardContent>
  </Card>
);
